import i18next from 'i18next';

import dayjs from '@/helpers/dayjs';
import { detectBrowserLocale } from '@/helpers/locale';

interface RelationshipData {
    id?: number | string;
    [key: string]: unknown;
}

export interface ShippingLocationRelationship {
    uuid: string;
    label: string;
    is_active: boolean;
    shipping_cost_surcharge?: number;
}

interface UserRelationships {
    company?: { data?: RelationshipData };
    workspace?: { data?: RelationshipData };
    shipping_locations?: { data?: ShippingLocationRelationship[] };
}

export interface Impersonation {
    uuid: string;
    mode: 'full' | 'read_only';
    expires_at: string;
    impersonator: {
        first_name: string;
        last_name: string;
    };
}

export interface User {
    id: number;
    uuid?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    locale?: string;
    allowed?: string[];
    is_simulating_client?: boolean;
    is_2fa_enabled?: boolean;
    refresh_required?: boolean;
    active_workspace_id?: number | null;
    price_display?: string;
    relationships?: UserRelationships;

    /** Set while an administrator is acting as this user. */
    impersonation?: Impersonation | null;
}

type SessionValues = Pick<User, 'active_workspace_id' | 'locale' | 'price_display'>;

let user: User | null = null;
// Anonymous visitors (login, register) follow the browser language; a
// logged-in user's profile locale replaces it in `setUser()`.
let locale: string = detectBrowserLocale(
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [
        navigator.language,
    ])
);
let deeplink: string | null = null;

// Reflect the locale on <html lang> so browser translators (e.g. Chrome's
// EN→ES auto-translate) don't misidentify the page as English and offer to
// "translate" an already-localised UI.
const applyDocumentLocale = (value: string) => {
    dayjs.locale(value);

    if (typeof document !== 'undefined') {
        document.documentElement.lang = value;
    }
};

const auth = {

    /**
     * The locale to start i18next with. Applies the date and document side
     * effects too, which `locale(value)` cannot do before i18next exists.
     */
    bootstrapLocale(): string {
        applyDocumentLocale(locale);

        return locale;
    },

    locale(value?: string): string {
        if (value) {
            locale = value;

            applyDocumentLocale(value);
            i18next.changeLanguage(value);
        }

        return locale;
    },

    can(permission: string): boolean {
        return !!(user?.allowed || []).find((p) => {
            return p === permission;
        });
    },

    canOneOf(permissions: string[]): boolean {
        return !!permissions.find((permission) => {
            return this.can(permission);
        });
    },

    cannot(permission: string): boolean {
        return !this.can(permission);
    },

    /**
     * Checks whether a session is anonymous.
     */
    isAnonymous(): boolean {
        return user === null;
    },

    /**
     * Checks whether the current user is a non-simulating administrator.
     */
    isAdministrator(): boolean {
        return !this.getWorkspace() && !this.isSimulatingClient();
    },

    /**
     * Checks whether an administrator is currently simulating a client workspace.
     */
    isSimulatingClient(): boolean {
        return !!user?.is_simulating_client;
    },

    /**
     * Checks whether an administrator is currently acting as this user.
     */
    isImpersonating(): boolean {
        return !!user?.impersonation;
    },

    getImpersonation(): Impersonation | null {
        return user?.impersonation ?? null;
    },

    /**
     * Checks whether a session is authenticated.
     */
    isIdentified(): boolean {
        return !this.isAnonymous();
    },

    getUser(): User | null {
        return user;
    },

    getCompany(): RelationshipData | undefined {
        return user?.relationships?.company?.data;
    },

    is2faDisabled(): boolean {
        return this.isAdministrator() && !this.is2faEnabled();
    },

    is2faEnabled(): boolean {
        return !!user?.is_2fa_enabled;
    },

    isRefreshRequired(): boolean {
        return !!user?.refresh_required;
    },

    /**
     * Returns the user fields that affect global session state (query caches, locale, pricing).
     * Used to detect session-impacting changes and to scope React Query cache keys.
     */
    sessionFields(): Array<keyof SessionValues> {
        return [
            'active_workspace_id',
            'locale',
            'price_display',
        ];
    },

    /**
     * Returns the current values of session-sensitive fields.
     * Use as part of React Query keys so cache is scoped per session context.
     */
    sessionValues(): SessionValues {
        const { active_workspace_id, locale: userLocale, price_display } = user ?? {};

        return { active_workspace_id, locale: userLocale, price_display };
    },

    setUser(value: User): void {
        user = { ...value };

        this.locale(user.locale);
    },

    clearSession(): void {
        user = null;
    },

    getUserId(): number {
        return user!.id;
    },

    getUserName(): string {
        const { first_name: firstName } = user!;

        return `${ firstName }`;
    },

    getWorkspace(): RelationshipData | undefined {
        return user?.relationships?.workspace?.data;
    },

    setDeeplink(path: string | null): void {
        // Certain path we don't want to set during refresh. Basically the ones that should go to `/home` anyway.
        if (
            path === '/refresh-session' ||
            path === '/request-password-reset' ||
            path === '/reset-password' ||
            path === '/confirm-login' ||
            path === '/login'
        ) {
            return;
        }

        deeplink = path;
    },

    getInitialPath(): string {
        return deeplink || '/';
    },
};

export default auth;
